<template>
    <div class="pagination-wrapper">
        <div class="pagination-left">
            <label for="recordsPerPage">Records per page:</label>
            <select id="recordsPerPage" v-model="recordsPerPage" @change="onRecordsPerPageChange">
                <option v-for="option in recordsPerPageOptions" :key="option" :value="option">
                    {{ option }}
                </option>
            </select>
            <span class="showing-range">
                Showing: {{ showingRange }} of {{ totalRecords }} records
            </span>
        </div>
        <ul class="pagination">
            <li class="pagination-item">
                <button type="button" @click="onClickFirstPage" :disabled="isInFirstPage">
                    << </button>
            </li>
            <li class="pagination-item">
                <button type="button" @click="onClickPreviousPage" :disabled="isInFirstPage">
                    < </button>
            </li>
            <!-- Visible Buttons Start -->
            <li v-for="page in pages" :key="page.name" class="pagination-item">
                <button type="button" @click="onClickPage(page.name)" :disabled="page.isDisabled"
                    :class="{ active: isPageActive(page.name) }">
                    {{ page.name }}
                </button>
            </li>
            <!-- Visible Buttons End -->
            <li class="pagination-item">
                <button type="button" @click="onClickNextPage" :disabled="isInLastPage">
                    >
                </button>
            </li>
            <li class="pagination-item">
                <button type="button" @click="onClickLastPage" :disabled="isInLastPage">
                    >> 
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
export default {
    name: "Pagination",
    props: {
        maxVisibleButtons: {
            type: Number,
            required: false,
            default: 3
        },
        totalPages: {
            type: Number,
            required: true
        },
        perPage: {
            type: Number,
            required: true
        },
        currentPage: {
            type: Number,
            required: true
        },
        totalRecords: {
            type: Number,
            required: true
        },
        defaultRecordsPerPage: {
            type: Number,
            required: false,
        },
        recordsPerPageOptions: {
            type: Array,
            required: false,
            default: () => [20, 50, 100]
        }
    },
    data() {
        return {
            recordsPerPage: this.perPage
        };
    },
    watch: {
        perPage(newVal) {
            this.recordsPerPage = newVal;
        }
    },
    mounted() {
    },
    computed: {
        startPage() {
            if (this.currentPage === 1) {
                return 1;
            }
            if (this.currentPage === this.totalPages) {
                let startPageNo = this.totalPages - this.maxVisibleButtons + 1;
                return startPageNo > 0 ? startPageNo : 1;
            }
            return this.currentPage - 1;
        },
        pages() {
            const range = [];
            for (
                let i = this.startPage; i <= Math.min(this.startPage + (this.maxVisibleButtons - 1), this.totalPages); i++
            ) {
                range.push({
                    name: i,
                    isDisabled: i === this.currentPage
                });
            }
            return range;
        },
        isInFirstPage() {
            return this.currentPage === 1;
        },
        isInLastPage() {
            return this.currentPage === this.totalPages;
        },
        showingRange() {
            const start = (this.currentPage - 1) * this.recordsPerPage + 1;
            const end = Math.min(this.currentPage * this.recordsPerPage, this.totalRecords);
            return `${start}-${end}`;
        }
    },
    methods: {
        onClickFirstPage() {
            this.$emit('pagechanged', 1);
        },
        onClickPreviousPage() {
            this.$emit('pagechanged', this.currentPage - 1);
        },
        onClickPage(page) {
            this.$emit('pagechanged', page);
        },
        onClickNextPage() {
            this.$emit('pagechanged', this.currentPage + 1);
        },
        onClickLastPage() {
            this.$emit('pagechanged', this.totalPages);
        },
        isPageActive(page) {
            return this.currentPage === page;
        },
        onRecordsPerPageChange() {
            this.$emit('recordCount', this.recordsPerPage);
            this.$emit('pagechanged', 1);
        }
    }
};
</script>


<style scoped>
/* === Pagination Wrapper === */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  background: var(--surface);
  font-family: "Inter", sans-serif;
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
}

/* === Left Section === */
.pagination-left {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 14px;
}

.pagination-left label {
  font-weight: 500;
  color: var(--text-primary);
}

.pagination-left select {
  height: 36px;
  padding: 6px 12px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: linear-gradient(145deg, var(--surface), var(--surface-hover));
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.25s ease;
}

.pagination-left select:hover {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 15%, transparent);
}

.pagination-left  select option{
    background-color: var(--body-bg);
    color: var(--text-primary);
    border: var(--border-color);
    border-radius: 10px;
}

.showing-range {
  font-size: 13px;
  color: var(--text-secondary);
}

/* === Pagination Buttons === */
.pagination {
  display: flex;
  align-items: center;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.pagination-item {
  display: flex;
}

.pagination-item button {
  height: 36px;
  min-width: 36px;
  padding: 0 12px;
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
}

.pagination-item button:hover:not(:disabled) {
  background: linear-gradient(145deg, var(--surface), var(--surface-hover));
  border-color: var(--primary);
  color: var(--primary);
  transform: translateY(-2px);
}

.pagination-item button:active:not(:disabled) {
  transform: scale(0.96);
}

/* === Active (Current Page) === */
.pagination-item button.active {
  background: linear-gradient(135deg, var(--primary), var(--primary-hover));
  border-color: var(--primary);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 3px 10px color-mix(in srgb, var(--primary) 35%, transparent);
  transform: scale(1.05);
}

/* === Disabled Buttons === */
.pagination-item button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: var(--surface-hover);
  color: var(--text-secondary);
}

/* === Responsive Adjustments === */
@media (max-width: 768px) {
  .pagination-wrapper {
    flex-direction: column;
    gap: 10px;
    border-radius: 12px;
    padding: 10px 0;
  }

  .pagination-left {
    justify-content: center;
    flex-wrap: wrap;
  }

  .pagination {
    flex-wrap: wrap;
    justify-content: center;
  }
}

/* === Dark Mode Overrides === */
[data-theme="dark"] .pagination-wrapper {
  background: var(--surface);
}

[data-theme="dark"] .pagination-item button.active {
  background: linear-gradient(135deg, var(--primary), var(--primary-hover));
  color: #fff;
}

[data-theme="dark"] .pagination-left select {
  background: linear-gradient(145deg, var(--surface-active), var(--surface));
  color: var(--text-primary);
}
</style>



